import React, { useMemo } from 'react';
import { getCampaignBreakdowns } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { readMetaAgeGender, readMetaGeo } from '../metaAnalysis';
import { isMetaTodayPeriod } from '../metaPeriod';
import { AgeCard, GenderCard, PlaceCard } from './MetaAudienceView';
import { MetaCard, MetaFootnote, MetaSoftNotice } from './MetaDataParts';

/**
 * «Público de todas las campañas del espacio» (pestaña Informe): lo mismo que Análisis → Público,
 * pero **sin campaña** (`getcampaignbreakdowns` sin `campaign_id` suma todas las campañas de las
 * cuentas seguidas; no es el público de una propiedad). Dos pedidos independientes
 * (`age_gender` y `geo` = país + región) y cinco tarjetas, cada una con su estado: cargando, no
 * disponible, error con «Reintentar» o vacío. Es un dato real de Meta (no aproximado).
 */
export default function MetaSpaceAudience({ period, ownerUserId = null }) {
  const deps = [period?.since, period?.until, ownerUserId];
  const ageState = useMetaRead((signal) => getCampaignBreakdowns({ period, breakdown: 'age_gender', ownerUserId, signal }), { deps });
  const geoState = useMetaRead((signal) => getCampaignBreakdowns({ period, breakdown: 'geo', ownerUserId, signal }), { deps });
  const age = useMemo(() => readMetaAgeGender(ageState.res), [ageState.res]);
  const geo = useMemo(() => readMetaGeo(geoState.res), [geoState.res]);
  if (ageState.status === 'unavailable' && geoState.status === 'unavailable') {
    return (
      <MetaCard title="Público de todas las campañas del espacio" testId="meta-report-audience">
        <MetaFootnote testId="meta-report-audience-unavailable">Todavía no hay datos de público: no están disponibles en este entorno.</MetaFootnote>
      </MetaCard>
    );
  }
  const ageReady = ageState.status === 'ready';
  const geoReady = geoState.status === 'ready';
  const mixed = (ageReady && age.mixedCurrencies) || (geoReady && geo.mixedCurrencies);
  return (
    <>
      <MetaCard title="Público de todas las campañas del espacio" testId="meta-report-audience">
        <MetaFootnote testId="meta-report-audience-note">
          Suma de todas las campañas de las cuentas que seguís, no de una propiedad en particular. Es el público que respondió a tus anuncios, según Meta: es solo informativo.
        </MetaFootnote>
        {isMetaTodayPeriod(period) ? (
          <MetaSoftNotice testId="meta-report-audience-today">Hoy los datos de público pueden estar incompletos; se completan mañana.</MetaSoftNotice>
        ) : null}
        {geoReady && geo.geoPending ? (
          <MetaSoftNotice testId="meta-report-audience-geo-pending">
            Países y regiones se completan una vez por día con la sincronización programada de las 6:00: hasta que corra, pueden faltar o estar incompletos.
          </MetaSoftNotice>
        ) : null}
        {mixed ? (
          <MetaSoftNotice testId="meta-report-audience-mixed">
            Hay anuncios en más de una moneda: se muestran contactos e impresiones, sin montos ni costos.
          </MetaSoftNotice>
        ) : null}
      </MetaCard>
      <AgeCard read={age} state={ageState} />
      <GenderCard read={age} state={ageState} />
      <PlaceCard
        title="Países" firstColumn="País" rows={geo.countries} geo={geo} state={geoState} testId="meta-report-countries"
        emptyText="Todavía no hay datos de países para este período."
        pendingText="Todavía no hay datos de países: se completan una vez por día, a las 6:00."
      />
      <PlaceCard
        title="Regiones" firstColumn="Región" rows={geo.regions} geo={geo} state={geoState} testId="meta-report-regions" contacts={geo.regionContacts}
        emptyText="Todavía no hay datos de regiones para este período."
        pendingText="Todavía no hay datos de regiones: se completan una vez por día, a las 6:00."
      />
    </>
  );
}
